/** @file Timeline actions behind the toolbar and the `timeline` key bindings. */
import { createSample } from '@pose2d/core/anim/createSample';
import { createPose } from '@pose2d/core/pose/createPose';
import { playheadStore } from '../../stores/playheadStore';
import { selectionStore } from '../../stores/selectionStore';
import { toolStore } from '../../stores/toolStore';
import { animationFrames } from '../../playback/animationFrames';
import type { EditorDeps } from '../../playback/editorDeps';
import { keyBonesCalls } from '../../playback/keyBonesCalls';
import { playhead } from '../../playback/playhead';
import { poseAt } from '../../playback/poseAt';
import { stopMixPreview } from '../viewport/stopMixPreview';
import { viewportApi } from '../viewport/viewportApi';
import { viewportStore } from '../viewport/viewportStore';
import { keyClipboardStore } from './keyClipboardStore';
import { keyCommands } from './model/keyCommands';

/** Action names; binding ids are `timeline.<name>`. */
export type TimelineAction =
  | 'playPause' | 'firstFrame' | 'prevFrame' | 'nextFrame' | 'lastFrame' | 'deleteKeys' | 'copyKeys' | 'pasteKeys' | 'keyBones' | 'toggleAutoKey' | 'clearLoop';

/** K: the viewport's held unkeyed pose when there is one (the same Key as the viewport's, R-E1-07); else rotation,
 *  translation and scale of the selected bones as the edited animation poses them at the playhead's whole frame, never
 *  a mix preview's pose (CB-05 B17: Key stops the preview, as a tool press does). Animate mode only. */
function keyBones(deps: EditorDeps): void {
  if (viewportStore.getState().unkeyed.length > 0) {
    viewportApi.commitUnkeyed();
    return;
  }
  const data = deps.data();
  const doc = deps.doc();
  const { animation } = playheadStore.getState();
  const frame = Math.max(0, Math.round(playheadStore.getState().frame));
  if (data === null || doc === null || animation === null || toolStore.getState().mode !== 'animate') return;
  const name = doc.animations[animation]?.name;
  const anim = name === undefined ? undefined : data.animationIndex.get(name);
  if (anim === undefined) return;
  stopMixPreview();
  const bones = selectionStore.getState().bones.flatMap((id) => {
    const index = data.boneIndex.get(doc.bones[id]?.name ?? '');
    return index === undefined ? [] : [{ id, index }];
  });
  const pose = createPose(data);
  poseAt(data, pose, [createSample(data)], { kind: 'animation', anim, time: frame / doc.meta.fps });
  deps.dispatch(keyBonesCalls(animation, frame, bones, pose.local));
}

/** End of the play range: the loop end, else the animation length. */
function lastFrame(deps: EditorDeps): number {
  const { animation, loop } = playheadStore.getState();
  const anim = animation === null ? undefined : deps.doc()?.animations[animation];
  return loop?.[1] ?? (anim === undefined ? 0 : animationFrames(anim));
}

/** One closure per action, over the editor links. */
export function timelineActions(deps: EditorDeps): Record<TimelineAction, () => void> {
  const keys = () => selectionStore.getState().keys;
  return {
    playPause: () => { playhead.toggle(); },
    firstFrame: () => { playhead.setFrame(playheadStore.getState().loop?.[0] ?? 0); },
    prevFrame: () => { playhead.step(-1); },
    nextFrame: () => { playhead.step(1); },
    lastFrame: () => { playhead.setFrame(lastFrame(deps)); },
    deleteKeys: () => {
      deps.dispatch(keyCommands.remove(keys()));
      selectionStore.setState({ keys: [] });
    },
    copyKeys: () => {
      const doc = deps.doc();
      if (doc !== null) keyClipboardStore.setState({ clip: keyCommands.copy(doc, keys()) });
    },
    pasteKeys: () => {
      const { clip } = keyClipboardStore.getState();
      const { animation, frame } = playheadStore.getState();
      if (clip !== null && animation !== null) deps.dispatch(keyCommands.paste(clip, animation, Math.round(frame)));
    },
    keyBones: () => { keyBones(deps); },
    toggleAutoKey: () => { playhead.setAutoKey(!playheadStore.getState().autoKey); },
    clearLoop: () => { playhead.setLoop(null); },
  };
}
